import { ApplicationState } from '../../ApplicationState';
import { TrustAgentType } from '../../dataStorage.model';
import {
  getPrimaryUserLegalEntityType,
  getPrimaryUserPartnerType,
  getPrimaryUserTrustAgentType,
} from '../DataStorageRawData/dataStorageRawData.selectors';
import { ExpensesDataSection } from '../Expenses/expenses.model';
import { UserType } from '../Session/session.model';
import { getLoggedInUserType } from '../Session/session.selectors';
import { individEntityForms } from './formsData.helpers';
import { CustomerRole, EntityLegalForm, FormDataSection, PartnerType } from './formsData.model';

export const formDataSectionOrder: FormDataSection[] = [
  FormDataSection.personalData,
  FormDataSection.contactInformation,
  FormDataSection.referenceContacts,
  FormDataSection.companyInformation,
  FormDataSection.employmentInformation,
  FormDataSection.assets,
  FormDataSection.liabilities,
];

export const expensesDataSectionOrder: ExpensesDataSection[] = [
  ExpensesDataSection.income,
  ExpensesDataSection.billsAndLivingExpenses,
  ExpensesDataSection.expenses,
];

const getCustomerPartnerType = (state: ApplicationState): PartnerType | null => {
  if (getLoggedInUserType(state) === UserType.CO_APPLICANT) {
    return getPrimaryUserPartnerType(state);
  }

  return state.formsData[FormDataSection.personalData].partnerType ?? null;
};

const getEntityLegalForm = (state: ApplicationState): EntityLegalForm | null => {
  if (getLoggedInUserType(state) === UserType.CO_APPLICANT) {
    return getPrimaryUserLegalEntityType(state);
  }

  return state.formsData[FormDataSection.personalData].companyData?.entityLegalForm ?? null;
};

const getTrustAgentType = (state: ApplicationState): TrustAgentType | null => {
  if (getLoggedInUserType(state) === UserType.CO_APPLICANT) {
    return getPrimaryUserTrustAgentType(state);
  }

  return state.formsData[FormDataSection.personalData].customerRelations?.[CustomerRole.TRUST]?.trustAgentType ?? null;
};

const isBusinessIndividual = (state: ApplicationState): boolean =>
  getCustomerPartnerType(state) === PartnerType.BUSINESS &&
  individEntityForms.includes(getEntityLegalForm(state) as EntityLegalForm);

const isCompanyDataSectionRelevant = (state: ApplicationState): boolean => {
  const entityLegalForm = getEntityLegalForm(state);

  if (!entityLegalForm) {
    return false;
  }

  if (entityLegalForm === EntityLegalForm.COMPANY && getLoggedInUserType(state) === UserType.PRIMARY) {
    return true;
  }

  if (entityLegalForm === EntityLegalForm.TRUST) {
    if (state.session.userType === UserType.PRIMARY) {
      return getTrustAgentType(state) === TrustAgentType.COMPANY;
    }

    if (state.session.userType === UserType.CO_APPLICANT) {
      return getTrustAgentType(state) === TrustAgentType.INDIVIDUAL;
    }
  }

  return false;
};

const isReferenceContactsSectionRelevant = (state: ApplicationState): boolean => {
  if (getLoggedInUserType(state) !== UserType.PRIMARY) {
    return false;
  }

  return getCustomerPartnerType(state) === PartnerType.PRIVATE || isBusinessIndividual(state);
};

const isEmploymentSectionRelevant = (state: ApplicationState): boolean =>
  getCustomerPartnerType(state) === PartnerType.PRIVATE || isBusinessIndividual(state);

export const getActiveFormSections = (state: ApplicationState): Record<FormDataSection, boolean> => ({
  [FormDataSection.personalData]: true,
  [FormDataSection.contactInformation]: true,
  [FormDataSection.referenceContacts]: isReferenceContactsSectionRelevant(state),
  [FormDataSection.companyInformation]: isCompanyDataSectionRelevant(state),
  [FormDataSection.employmentInformation]: isEmploymentSectionRelevant(state),
  [FormDataSection.assets]: true,
  [FormDataSection.liabilities]: true,
});
